import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../../assets/Icons";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../../components/_utils/CommonToast";
import { formatAccountNumber } from "../../../../../components/_utils/FormatAccountNumber";
import FormatAmount from "../../../../../components/_utils/FormatAmount/FormatAmount";
import RecreatePromptModalPopup from "../../../../../components/_utils/RecreatePromptModalPopup";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { internalTransactionsReject } from "../../../../../redux/features/transactions/internalTransactions";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";

const normalizeBaseErrors = (raw, txnList) => {
  if (!raw) return [];

  const findTxn = (id) => txnList?.find((txn) => txn?.txn_id === id);

  const result = [];

  if (Array.isArray(raw) && typeof raw[0] === "string") {
    raw.forEach((message, index) => {
      result.push({
        reasons: [message],
        txn: txnList?.[index],
      });
    });

    return result;
  }

  if (Array.isArray(raw)) {
    raw.forEach((item, index) => {
      const txn = findTxn(item?.txn_id) || txnList?.[index];

      const reasons = [];

      if (Array.isArray(item?.errors)) {
        item.errors.forEach((error) => {
          if (typeof error === "string") {
            reasons.push(error);
          } else if (error?.reason) {
            if (Array.isArray(error.reason)) {
              reasons.push(...error.reason);
            } else if (typeof error.reason === "string") {
              reasons.push(error.reason);
            }
          }
        });
      }

      if (reasons.length) {
        result.push({
          txn,
          reasons,
        });
      }
    });
  }

  return result;
};

const getErrorText = (error) => {
  if (typeof error === "string") {
    return error;
  }

  if (error === null || error === undefined) {
    return "Something went wrong";
  }

  if (Array.isArray(error)) {
    return error
      .map((item) => getErrorText(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof error === "object") {
    if (error?.reason) {
      return getErrorText(error.reason);
    }

    if (error?.message) {
      return getErrorText(error.message);
    }

    if (error?.errors) {
      return getErrorText(error.errors);
    }

    return JSON.stringify(error);
  }

  return String(error);
};

const InternalMultiApprovalFailed = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loadingBox, setLoadingBox] = useState(false);
  const [errorPageData, setErrorPageData] = useState([]);
  const [selectedTxnId, setSelectedTxnId] = useState("");
  const [recreationMode, setRecreationMode] = useState(null);
  const [showRecreateModal, setShowRecreateModal] = useState(false);

  const { internalTransactionsApproveError } = useSelector(
    (state) => state.internalTransactions,
  );

  const rawErrorData = JSON.parse(sessionStorage.getItem("internalTxnError"));

  const normalizedErrors = normalizeBaseErrors(rawErrorData, errorPageData);

  const handleRecreateAll = () => {
    setSelectedTxnId("");
    setRecreationMode("all");
    setShowRecreateModal(true);
  };

  // const handleRecreateSingle = (txnId) => {
  //   setSelectedTxnId(txnId);
  //   setRecreationMode("single");
  //   setShowRecreateModal(true);
  // };

  const closeRecreateModal = () => {
    if (loadingBox) return;

    setShowRecreateModal(false);
    setSelectedTxnId("");
    setRecreationMode(null);
  };

  const handleRecreationConfirmation = async (reason) => {
    setLoadingBox(true);

    try {
      const transactionIds =
        recreationMode === "all"
          ? errorPageData
              ?.filter((txn) => txn?.lifecycle_status !== "rejected")
              .map((txn) => txn?.txn_id)
              .filter(Boolean)
          : selectedTxnId;

      const response = await dispatch(
        internalTransactionsReject({
          id: transactionIds,
          rejection_reason: reason,
        }),
      );

      if (response?.payload?.status) {
        CommonToast({
          text: "Transaction rejected and sent for recreation.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });

        navigate(
          "/internal_transaction_dashboard/internal_transaction_multi_approval/multi_approval/declined",
          {
            replace: true,
          },
        );
      } else {
        CommonToast({
          text: getErrorText(
            response?.payload?.errors?.base || response?.payload?.message,
          ),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
    } finally {
      setLoadingBox(false);
      setShowRecreateModal(false);
      setSelectedTxnId("");
      setRecreationMode(null);
    }
  };

  const buttonRows = [
    [
      {
        btnText: "Send all for recreation",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--negative-bg)",
        icon: ICONS?.RECREATE_WHITE,
        imgColor: "var(--negative-bg)",
        handleFunction: handleRecreateAll,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        hoverOption: "dark",
      },
    ],
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: () => navigate("/internal_transaction_dashboard"),
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "dark",
      },
    ],
  ];

  const buildTransactionRows = (txn) => [
    {
      leftTitle: (
        <span className="font-semibold">
          INR <FormatAmount price={txn?.amount || 0} />
        </span>
      ),
      leftSubTitle: "Amount",
      rightIcon: true,
    },
    {
      leftTitle: formatAccountNumber(txn?.owner_account_bank_acc_no),
      leftSubTitle: "Source account",
      ifsc: txn?.owner_account_ifsc_code,
    },
    {
      leftTitle: formatAccountNumber(txn?.internal_account_bank_acc_no),
      leftSubTitle: "Destination account",
      ifsc: txn?.internal_account_ifsc_code,
    },
  ];

  const TransactionList = ({ transactions }) => (
    <>
      {transactions?.map((txn, index) => (
        <InfoCard
          key={txn?.txn_id || index}
          bgColor="var(--negative-card)"
          borderColor="var(--negative-bg)"
          hoverable="white"
          showRowDivider={true}
          rowBorderColor="var(--negative-bg)"
          onClick={() =>
            navigate(
              `/internal_transaction_dashboard/internal_transaction_view_all/internal_transaction_detail_page/${txn?.txn_id}`,
            )
          }
          rows={buildTransactionRows(txn)}
        />
      ))}
    </>
  );

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const storedSelectedApproval = JSON.parse(
      sessionStorage.getItem("selectedData"),
    );

    if (storedSelectedApproval) {
      setErrorPageData(storedSelectedApproval?.selectedSendTransactions || []);
    } else {
      setErrorPageData([]);
    }
  }, []);

  return (
    <>
      {showRecreateModal && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
          <RecreatePromptModalPopup
            loadingBox={loadingBox}
            inputDisabled={loadingBox}
            onConfirmBtnClick={handleRecreationConfirmation}
            onCancelBtnClick={closeRecreateModal}
          />
        </div>
      )}

      <StatusContainer
        status="error"
        buttonRows={buttonRows}
        title={
          <>
            Oops!&nbsp;
            {`Transaction ${
              internalTransactionsApproveError ? "Approval" : "Rejection"
            } failed`}
          </>
        }
        subtitle={
          <>
            An error occurred with the following transactions, causing the{" "}
            {internalTransactionsApproveError ? "approval" : "rejection"} of all
            the transactions to fail.
          </>
        }
      >
        <>
          <TabsWeapperSection title="Error Details">
            <>
              {normalizedErrors.length >
              GlobalConfiguration?.lengthCheckZero ? (
                normalizedErrors.map((errorData, index) => (
                  <InfoCard
                    key={errorData?.txn?.txn_id || index}
                    bgColor="var(--negative-card)"
                    borderColor="var(--negative-bg)"
                    showRowDivider={true}
                    rowBorderColor="var(--negative-bg)"
                    rows={[
                      {
                        leftTitle: formatAccountNumber(
                          errorData?.txn?.owner_account_bank_acc_no,
                        ),
                        leftSubTitle: "Source account",
                        ifsc: errorData?.txn?.owner_account_ifsc_code,
                      },
                      {
                        leftTitle: formatAccountNumber(
                          errorData?.txn?.internal_account_bank_acc_no,
                        ),
                        leftSubTitle: "Destination account",
                        ifsc: errorData?.txn?.internal_account_ifsc_code,
                      },
                      ...errorData.reasons.map((reason) => ({
                        leftTitle: reason,
                        leftSubTitle: "Error message",
                      })),
                    ]}
                  />
                ))
              ) : (
                <InfoCard
                  bgColor="var(--negative-card)"
                  variant="text"
                  label="No transactions found."
                  subLabel="No error details available."
                />
              )}
            </>
          </TabsWeapperSection>

          <TabsWeapperSection title="Transaction Details">
            <TransactionList transactions={errorPageData} />
          </TabsWeapperSection>
        </>
      </StatusContainer>
    </>
  );
};

export default InternalMultiApprovalFailed;
